import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Row, Col, Card, Collapse, Form, Container } from 'react-bootstrap';
import { SingleSelectAsync, SelectOption } from './SIngleSelectAsync';
import { MultiSelectAsync } from './MultiSelectAsync';
import FeatureNav from './FeatureNav';
import FooterSection from './FooterSection';
import HeaderComponent from './HeaderComponent';
import BreadCrumbs from './BreadCrumbs';
import Loader from './Loader';
import { prodApiEndpointUrl, stagingApiEndpointUrl } from 'types/apiEndpoints';
import { Tabulator, PageModule, DownloadModule, ExportModule, SortModule } from "tabulator-tables";
import "tabulator-tables/dist/css/tabulator.min.css";
import * as d3 from "d3";

Tabulator.registerModule([PageModule, DownloadModule, ExportModule, SortModule]);

interface CuratedJob {
	class: string;
	class_id: number;
	fiscal_year: number;
  curated_group: number;
}

interface MostCommonJob {
	entity_id: number;
	entity_name: string;
	fiscal_year: number;
	govt_lvl: string;
	wages: number;
	benefits: number;
	total: number;
	title: string;
	employee_name: string;
	class: string;
	class_id: number;
}

interface Summary {
	observations?: number;
	mean?: number;
	standard_deviation?: number;
	total_sum?: number;
}

interface Percentiles {
	minimum?: number;
	twenty_fifth_percentile?: number;
	median?: number;
	seventy_fifth_percentile?: number;
	maximum?: number;
}

interface StatisticsCardProps {
	title: string;
	value?: number;
	bg?: "secondary" | "primary";
	isCurrency?: boolean;
}

const EmployeesLocalGovernmentJobs = () => {
  const [showHeaderInfo, setShowHeaderInfo] = useState<boolean>(false);
  const [selectedJob, setSelectedJob] = useState<SelectOption | null>(null);
  const [selectedCuratedFiscalYear, setSelectedCuratedFiscalYear] = useState<SelectOption | null> (null);
  const [curatedOptions, setCuratedOptions] = useState<SelectOption[]>([]);
  const [curatedJobs, setCuratedJobs] = useState<CuratedJob[]>([]);
  const [masterData, setMasterData] = useState<MostCommonJob[]>([]);
  const [selectedClassId, setSelectedClassId] = useState<Number | null>(null);
  const [selectedEntityName, setSelectedEntityName] = useState<string | null>(null);
  const [selectedEntityNames, setSelectedEntityNames] = useState<string[] | null>(null);
  const [selectedEmployeeNames, setSelectedEmployeeNames] = useState<string[] | null>(null);
  const [allEmployeeNames, setAllEmployeeNames] = useState<string[] | null>(null);
  const [allEmployeeTitles, setAllEmployeeTitles] = useState<string[] | null>(null);
  const [allGovernmentTypes, setAllGovernmentTypes] = useState<string[] | null>(null);
  const [allFiscalYears, setAllFiscalYears] = useState<number[] | null>(null);
  const [allEntityNames, setAllEntityNames] = useState<string[] | null>(null);
  const [selectedEmployeeTitles, setSelectedEmployeeTitles] = useState<string[] | null> (null);
  const [selectedGovernmentTypes, setSelectedGovernmentTypes] = useState<string[] | null>(null);
  const [selectedFiscalYears, setSelectedFiscalYears] = useState<number[] | null>(null);
  const [filteredData, setFilteredData] = useState<MostCommonJob[] | null>(null);
  const [summaryData, setSummaryData] = useState<Summary | null>(null);
  const [filteredSummaryData, setFilteredSummaryData] = useState<Summary | null>(null);
  const [filteredPercentileData, setFilteredPercentileData] = useState<Percentiles | null>(null);
  const [percentileData, setPercentileData] = useState<Percentiles | null>(null);
  const [apiEndpoint] = useState<string>(() => {
    return window.location.hostname === "transparent.utah.gov" ? prodApiEndpointUrl : stagingApiEndpointUrl;
  });
  const chartRef = useRef<SVGSVGElement>(null);
  const legendRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const tabulatorRef = useRef<InstanceType<typeof Tabulator> | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const [chartType, setChartType] = useState<"Total Compensation" | "Wages Only" | "Benefits Only" | null>("Total Compensation");
  const [minimumRangeSalary, setMinimumRangeSalary] = useState<number> (0);
  const [maximumRangeSalary, setMaximumRangeSalary] = useState<number> (0);
  const [numberOfBins, setNumberOfBins] = useState<number> (67);
  const [notEnoughBins, setNotEnoughBins] = useState<boolean>(false);
  const [xAxisType, setAxisType] = useState<"Normal Scale" | "Log2 Transform">("Normal Scale");
  const [loading, setLoading] = useState<boolean>(false);
  const [rangeFilterFloor, setRangeFilterFloor] = useState<number>(0);
  const [rangeFilterCeiling, setRangeFilterCeiling] = useState<number>(0);

  const handleDownload = (type: "csv" | "xlsx" | "html" | "json") => {  
    const formattedEntityName = selectedEntityName?.toLowerCase()?.replaceAll(" ", "_") || "Entity";
    if (!tabulatorRef.current) return;
    tabulatorRef.current.download(type, `${formattedEntityName}_highest_paid.${type}`);
  };

  const loadJobOptions = async (inputValue: string): Promise<SelectOption[]> => {
    if (inputValue) {
      return curatedOptions.filter(option =>
          option.label.toLowerCase().includes(inputValue.toLowerCase())
      )
    }
    
    return curatedOptions; 
  };

  // Updated version with dynamic years
  const currentCuratedFiscalYearOptions = useMemo(() => {
    const uniqueYears = [...new Set(curatedJobs.map(item => item.fiscal_year).filter(Boolean))];

    return uniqueYears
    .sort((a, b) => b - a)
    .map(year => ({
      value: year.toString(),
      label: year.toString()
    }));
  }, [curatedJobs]);

  // Updated Version with curated common local government titles
  const curatedJobTitleSearchString = "/?function=getCuratedLGTitles&parameter=%7B%7D";

  // Fetch all curated jobs on mount
  useEffect(() => {
    setLoading(true);
    const controller = new AbortController()
    const signal = controller.signal;

    const fetchAllCuratedJobs = async () => {
      try {
        const buildCuratedJobsApiUrl = (apiEndpointUrl: string = apiEndpoint, searchParameter: string = curatedJobTitleSearchString) => {
          return `${apiEndpointUrl}${searchParameter}`;
        }

        const res = await fetch(buildCuratedJobsApiUrl(), { signal });
        const curatedData = res.ok ? await res.json() : [];

        if (!signal.aborted && Array.isArray(curatedData)) {
          const uniqueClasses = [...new Set(curatedData.map((item: CuratedJob) => item.class))] as string[];

          const formattedOptions: SelectOption[] = uniqueClasses.map(className => ({
            value: className,
            label: className
          }));

          setCuratedOptions(formattedOptions);
          setCuratedJobs(curatedData);
        }
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          console.error("Fetch error fetchAllCuratedJobs");
        }
      } finally {
        if (!signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchAllCuratedJobs();
    // Added to clean up the fetch if user leaves the page
    return () => controller.abort();
  }, []);

  // Update Selected Class ID when user selects a new job title
  useEffect(() => {
    const jobTitle = selectedJob?.value;

    // Resetting filters when a new job gets selected
    setSelectedEmployeeTitles(null);
    setSelectedEmployeeNames(null);
    setSelectedGovernmentTypes(null);
    setSelectedEntityNames(null);
    setSelectedFiscalYears(null);

    if (jobTitle) {
      const found = curatedJobs.find(item => item.class === jobTitle);
      const idNumber = found?.class_id ?? null;
      setSelectedClassId(idNumber);

      const allMatches = curatedJobs.filter(item => item.class === jobTitle);
    } else {
      setSelectedClassId(null);
    }
  }, [selectedJob, curatedJobs]);

  // Updated - Initialize Tabulator Table and add data
  useEffect(() => {
    if (!tableRef.current) return;

    setLoading(true);
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchJobsWithClassId = async () => {
      try {
        const yearValue = selectedCuratedFiscalYear?.value || "2025";
        const payload = { 
          class_id: selectedClassId, 
          fiscal_year: yearValue 
        };

        const classIdEncodedParam = encodeURIComponent(JSON.stringify(payload));
        const jobsWithClassIdSearchString = `/?function=getLocalGovJobTitle&parameter=${classIdEncodedParam}`;

        const buildJobsWIthClassIdApiUrl = (apiEndpointUrl: string = apiEndpoint, searchParameter: string = jobsWithClassIdSearchString) => {
          return `${apiEndpointUrl}${searchParameter}`;
        }

        const res = await fetch(buildJobsWIthClassIdApiUrl(), { signal });
        const classIdFilteredData = res.ok ? await res.json() : [];
        setMasterData(classIdFilteredData);

        // Universal Data Length
        const dataLength = classIdFilteredData.length;

        // Implement Chart Type into summary data
        switch (chartType) {
          case "Total Compensation":
            // Summary Data
            const totalCompDataLength = classIdFilteredData.length;
            const totalCompWages = classIdFilteredData.reduce((prev: number, current: any) => {
              return Math.round(prev + current.total);
            }, 0);
            const totalCompMean = Math.round(totalCompWages / totalCompDataLength);
            const totalCompVariance = classIdFilteredData.reduce((prev: number, current: any) => {
              const difference = current.total - totalCompMean;
              const squaredDifference = Math.pow(difference, 2);
              return prev + squaredDifference;
            }, 0) / totalCompDataLength;
            const totalCompStandardDeviation = Math.round(Math.sqrt(totalCompVariance));

            setSummaryData({
              observations: totalCompDataLength,
              mean: totalCompMean,
              standard_deviation: totalCompStandardDeviation,
              total_sum: totalCompWages,
            });

            // Percentile Data
            const totalCompSortedWages = classIdFilteredData.map((item: any) => Math.round(item.total)).sort((a: any , b: any) => a - b);
            const totalCompLowestWage = totalCompSortedWages[0];
            const totalCompTwentyFifthindex = Math.floor(totalCompSortedWages.length * 0.25);
            const totalCompSeventyFifthindex = Math.floor(totalCompSortedWages.length * 0.75);
            const totalCompTwentyFifthPercentile = totalCompSortedWages[totalCompTwentyFifthindex];
            const totalCompSeventyFifthPercentile = totalCompSortedWages[totalCompSeventyFifthindex];
            const totalCompMid = Math.floor(totalCompDataLength / 2);
            const totalCompMedian = totalCompDataLength % 2 !== 0 
              ? totalCompSortedWages[totalCompMid] 
              : (totalCompSortedWages[totalCompMid - 1] + totalCompSortedWages[totalCompMid]) / 2;
            const totalCompMaximum = totalCompSortedWages[totalCompSortedWages.length - 1];

            setPercentileData({
              minimum: totalCompLowestWage,
              twenty_fifth_percentile: totalCompTwentyFifthPercentile,
              median: totalCompMedian,
              seventy_fifth_percentile: totalCompSeventyFifthPercentile,
              maximum: totalCompMaximum,
            })
            break;

          case "Wages Only":
            // Summary Data
            const wagesOnlyDataLength = classIdFilteredData.length;
            const wagesOnlyTotalWages = classIdFilteredData.reduce((prev: number, current: any) => {
              return Math.round(prev + current.wages);
            }, 0);
            const wagesOnlyMean = Math.round(wagesOnlyTotalWages / wagesOnlyDataLength);
            const wagesOnlyVariance = classIdFilteredData.reduce((prev: number, current: any) => {
              const wagesOnlyDifference = current.wages - wagesOnlyMean;
              const wagesOnlysquaredDifference = Math.pow(wagesOnlyDifference, 2);
              return prev + wagesOnlysquaredDifference;
            }, 0) / wagesOnlyDataLength;
            const wagesOnlyStandardDeviation = Math.round(Math.sqrt(wagesOnlyVariance));

            setSummaryData({
              observations: wagesOnlyDataLength,
              mean: wagesOnlyMean,
              standard_deviation: wagesOnlyStandardDeviation,
              total_sum: wagesOnlyTotalWages,
            });

            // Percentile Data
            const wagesOnlySortedWages = classIdFilteredData.map((item: any) => Math.round(item.wages)).sort((a: any , b: any) => a - b);
            const wagesOnlyLowestWage = wagesOnlySortedWages[0];
            const wagesOnlyTwentyFifthindex = Math.floor(wagesOnlySortedWages.length * 0.25);
            const wagesOnlySeventyFifthindex = Math.floor(wagesOnlySortedWages.length * 0.75);
            const wagesOnlyTwentyFifthPercentile = wagesOnlySortedWages[wagesOnlyTwentyFifthindex];
            const wagesOnlySeventyFifthPercentile = wagesOnlySortedWages[wagesOnlySeventyFifthindex];
            const wagesOnlyMid = Math.floor(wagesOnlyDataLength / 2);
            const wagesOnlyMedian = wagesOnlyDataLength % 2 !== 0 
              ? wagesOnlySortedWages[wagesOnlyMid] 
              : (wagesOnlySortedWages[wagesOnlyMid - 1] + wagesOnlySortedWages[wagesOnlyMid]) / 2;
            const wagesOnlyMaximum = wagesOnlySortedWages[wagesOnlySortedWages.length - 1];

            setPercentileData({
              minimum: wagesOnlyLowestWage,
              twenty_fifth_percentile: wagesOnlyTwentyFifthPercentile,
              median: wagesOnlyMedian,
              seventy_fifth_percentile: wagesOnlySeventyFifthPercentile,
              maximum: wagesOnlyMaximum,
            })
            break;

          case "Benefits Only":
            // Summary Data
            const benefitsOnlyDataLength = classIdFilteredData.length;
            const benefitsOnlyTotalWages = classIdFilteredData.reduce((prev: number, current: any) => {
              return Math.round(prev + current.benefits);
            }, 0);
            const benefitsOnlyMean = Math.round(benefitsOnlyTotalWages / benefitsOnlyDataLength);
            const benefitsOnlyVariance = classIdFilteredData.reduce((prev: number, current: any) => {
              const difference = current.benefits - benefitsOnlyMean;
              const benefitsOnlySquaredDifference = Math.pow(difference, 2);
              return prev + benefitsOnlySquaredDifference;
            }, 0) / benefitsOnlyDataLength;
            const benefitsOnlyStandardDeviation = Math.round(Math.sqrt(benefitsOnlyVariance));

            setSummaryData({
              observations: benefitsOnlyDataLength,
              mean: benefitsOnlyMean,
              standard_deviation: benefitsOnlyStandardDeviation,
              total_sum: benefitsOnlyTotalWages,
            });

            // Percentile Data
            const benefitsOnlySortedWages = classIdFilteredData.map((item: any) => Math.round(item.benefits)).sort((a: any , b: any) => a - b);
            const benefitsOnlyLowestWage = benefitsOnlySortedWages[0];
            const benefitsOnlyTwentyFifthindex = Math.floor(benefitsOnlySortedWages.length * 0.25);
            const benefitsOnlySeventyFifthindex = Math.floor(benefitsOnlySortedWages.length * 0.75);
            const benefitsOnlyTwentyFifthPercentile = benefitsOnlySortedWages[benefitsOnlyTwentyFifthindex];
            const benefitsOnlySeventyFifthPercentile = benefitsOnlySortedWages[benefitsOnlySeventyFifthindex];
            const benefitsOnlyMid = Math.floor(benefitsOnlyDataLength / 2);
            const benefitsOnlyMedian = dataLength % 2 !== 0 
              ? benefitsOnlySortedWages[benefitsOnlyMid] 
              : Math.round((benefitsOnlySortedWages[benefitsOnlyMid - 1] + benefitsOnlySortedWages[benefitsOnlyMid]) / 2);
            const benefitsOnlyMaximum = benefitsOnlySortedWages[benefitsOnlySortedWages.length - 1];

            setPercentileData({
              minimum: benefitsOnlyLowestWage,
              twenty_fifth_percentile: benefitsOnlyTwentyFifthPercentile,
              median: benefitsOnlyMedian,
              seventy_fifth_percentile: benefitsOnlySeventyFifthindex,
              maximum: benefitsOnlyMaximum,
            })
            break;
          default:
        }

        // Sorted variables for filter creation
        const sortedEmployeeTitles = [...new Set(classIdFilteredData.map((item: any) => item.title))]
          .sort((a: any, b: any) => a.localeCompare(b)) as string[];
        setAllEmployeeTitles(sortedEmployeeTitles);

        const sortedEmployeeNames = [...new Set(classIdFilteredData.map((item: any) => item.employee_name))]
          .sort((a: any, b: any) => a.localeCompare(b)) as string[];
        setAllEmployeeNames(sortedEmployeeNames);
        
        const sortedGovernmentTypes = [...new Set(classIdFilteredData.map((item: any) => item.govt_lvl))]
          .sort((a: any, b: any) => a.localeCompare(b)) as string[];
        setAllGovernmentTypes(sortedGovernmentTypes);

        const sortedEntityNames = [...new Set(classIdFilteredData.map((item: any) => item.entity_name))]
          .sort((a: any, b: any) => a.localeCompare(b)) as string[];
        setAllEntityNames(sortedEntityNames);

        const sortedFiscalYears = [...new Set(classIdFilteredData.map((item: any) => Number(item.fiscal_year)))]
          .sort((a: any, b: any) => b - a) as number[];
        setAllFiscalYears(sortedFiscalYears);

        if (!signal.aborted && Array.isArray(classIdFilteredData)) {
          let tableData = classIdFilteredData;
          if (selectedEmployeeTitles && selectedEmployeeTitles.length > 0) {
            tableData = classIdFilteredData.filter(item => 
              selectedEmployeeTitles.includes(item.title)
            );
          }

          setFilteredData(tableData);

          const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) && window.innerWidth < 1025;

          tabulatorRef.current = new Tabulator(tableRef.current, {
            data: classIdFilteredData,
            minHeight: "450px",
            maxHeight: "100%",
            layout: isMobile ? "fitDataFill" : "fitColumns",
            pagination: "local",
            paginationSize: 10,
            placeholder: "No Data Available",
            headerSort: true,
            initialSort: [
              {
                column: "total",
                dir: "desc",
              }
            ],
            columns: [
              {
                titleFormatter: () => {
                  const el = document.createElement("span");
                  el.innerHTML = "<i class='fas fa-briefcase'></i> Title";
                  return el;
                },
                title: "Title",
                field: "title",
              },
              {
                titleFormatter: () => {
                  const el = document.createElement("span");
                  el.innerHTML = "<i class='fas fa-user'></i> Employee";
                  return el;
                },
                title: "Employee",
                field: "employee_name",
              },
              {
                titleFormatter: () => {
                  const el = document.createElement("span");
                  el.innerHTML = "<i class='fas fa-city'></i> Gov. Type";
                  return el;
                },
                title: "Government",
                field: "govt_lvl",
              },
              {
                titleFormatter: () => {
                  const el = document.createElement("span");
                  el.innerHTML = "<i class='fas fa-landmark'></i> Entity";
                  return el;
                },
                title: "Entity",
                field: "entity_name",
              },
              {
                titleFormatter: () => {
                  const el = document.createElement("span");
                  el.innerHTML = "<i class='fas fa-calendar'></i> Year";
                  return el;
                },
                title: "Fiscal Year",
                field: "fiscal_year",
              },
              {
                titleFormatter: () => {
                  const el = document.createElement("span");
                  el.innerHTML = "<i class='fas fa-dollar-sign'></i> Wages";
                  return el;
                },
                title: "Wages",
                field: "wages",
                sorter: "number",
                formatter: "money",
                headerSort: true,
                formatterParams: { symbol: "$" }
              },
              {
                titleFormatter: () => {
                  const el = document.createElement("span");
                  el.innerHTML = "<i class='fas fa-dollar-sign'></i> Benefits";
                  return el;
                },
                title: "Benefits",
                field: "benefits",
                formatter: "money",
                formatterParams: { symbol: "$" }
              },
              {
                titleFormatter: () => {
                  const el = document.createElement("span");
                  el.innerHTML = "<i class='fas fa-dollar-sign'></i> Total";
                  return el;
                },
                title: "Total",
                field: "total",
                sorter: "number",
                formatter: "money",
                formatterParams: { symbol: "$" }
              },
            ],
          });
        }
      } catch (err: any) {
        console.error("Fetch error getEmployeePayInfo()");
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    };

    if (selectedClassId) {
      fetchJobsWithClassId();
    }

    const handleResize = () => {
      if (!tabulatorRef.current) return;
      tabulatorRef.current.setOptions({
        layout: window.innerWidth < 1025 ? "fitDataFill" : "fitColumns"
      });
      tabulatorRef.current.redraw(true);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      controller.abort();
      window.removeEventListener("resize", handleResize);
      if (tabulatorRef.current) {
        tabulatorRef.current.destroy();
        tabulatorRef.current = null;
      }
    };
  }, [selectedClassId, chartType, selectedEmployeeTitles, selectedEmployeeNames, selectedGovernmentTypes, selectedEntityNames, selectedFiscalYears, selectedCuratedFiscalYear]);

  // Update Table when masterData updates, scan masterData to establish min and max for slider boundaries
  useEffect(() => {
    if (masterData.length > 0) {
      const totals = masterData.map(item => item.total);

      if(totals.length > 0) {
        const minValue = Math.min(...totals);
        const maxValue = Math.max(...totals);

        setMinimumRangeSalary(minValue);
        setMaximumRangeSalary(maxValue);

        setRangeFilterFloor(minValue);
        setRangeFilterCeiling(maxValue);
      }
    }
  }, [masterData]);

  // Implement filters and update Tabulator table accordingly
  useEffect(() => {
    if (masterData.length === 0) return;
    let data = [...masterData];

    // Filter Employee Title
    if (selectedEmployeeTitles && selectedEmployeeTitles.length > 0) {
      data = data.filter(item => selectedEmployeeTitles.includes(item.title));
    }

    // Filter Employee Name
    if (selectedEmployeeNames && selectedEmployeeNames.length > 0) {
      data = data.filter(item => selectedEmployeeNames.includes(item.employee_name));
    }

    // Filter Government Type
    if (selectedGovernmentTypes && selectedGovernmentTypes.length > 0) {
      data = data.filter(item => selectedGovernmentTypes.includes(item.govt_lvl));
    }

    // Entity Name Filter
    if (selectedEntityNames && selectedEntityNames.length > 0) {
        data = data.filter(item => selectedEntityNames.includes(item.entity_name));
    }

    // Fiscal Year Filter
    if (selectedFiscalYears && selectedFiscalYears.length > 0) {
      data = data.filter(item => selectedFiscalYears.includes(Number(item.fiscal_year)));
    }

    // Filter Maximum Range Salary
    if (maximumRangeSalary > 0) {
      data = data.filter(item => item.total <= maximumRangeSalary);
    }

    // Filter Minimum Range Salary
    if (minimumRangeSalary > 0) {
      data = data.filter(item => item.total >= minimumRangeSalary);
    }

    const filteredDataLength = data.length;

    if (filteredDataLength > 0) {
      switch (chartType) {
        case "Total Compensation":
          // Filtered Total Compensation Summary Data
          const filteredTotalCompWages = data.reduce((prev: number, current: any) => {
          return Math.round(prev + current.total);
          }, 0);
          const filteredTotalCompMean = Math.round(filteredTotalCompWages / filteredDataLength);
          const filteredTotalCompVariance = data.reduce((prev: number, current: any) => {
            const filteredTotalCompDifference = current.total - filteredTotalCompMean;
            const filteredTotalCompSquaredDifference = Math.pow(filteredTotalCompDifference, 2);
            return prev + filteredTotalCompSquaredDifference;
          }, 0) / filteredDataLength;
          const filteredTotalCompStandardDeviation = Math.round(Math.sqrt(filteredTotalCompVariance));

          setFilteredSummaryData({
            observations: filteredDataLength,
            mean: filteredTotalCompMean,
            standard_deviation: filteredTotalCompStandardDeviation,
            total_sum: filteredTotalCompWages,
          });

          // Filtered Total Compensation Percentile Data
          const filteredTotalCompSortedWages = data.map((item: any) => Math.round(item.total)).sort((a: any , b: any) => a - b);
          const filteredTotalCompLowestWage = filteredTotalCompSortedWages[0];
          const filteredTotalCompTwentyFifthindex = Math.floor(filteredTotalCompSortedWages.length * 0.25);
          const filteredTotalCompSeventyFifthindex = Math.floor(filteredTotalCompSortedWages.length * 0.75);
          const filteredTotalCompTwentyFifthPercentile = filteredTotalCompSortedWages[filteredTotalCompTwentyFifthindex];
          const filteredTotalCompSeventyFifthPercentile = filteredTotalCompSortedWages[filteredTotalCompSeventyFifthindex];
          const filteredTotalCompMid = Math.floor(filteredDataLength / 2);
          const filteredTotalCompMedian = filteredDataLength % 2 !== 0 
            ? filteredTotalCompSortedWages[filteredTotalCompMid] 
            : (filteredTotalCompSortedWages[filteredTotalCompMid - 1] + filteredTotalCompSortedWages[filteredTotalCompMid]) / 2;
          const filteredTotalCompMaximum = filteredTotalCompSortedWages[filteredTotalCompSortedWages.length - 1];

          // Update for Range Filters
          setFilteredPercentileData({
            minimum: filteredTotalCompLowestWage,
            twenty_fifth_percentile: filteredTotalCompTwentyFifthPercentile,
            median: filteredTotalCompMedian,
            seventy_fifth_percentile: filteredTotalCompSeventyFifthPercentile,
            maximum: filteredTotalCompMaximum,
          })
          break;
      
        case "Wages Only":
          // Filtered Wages Only Summary Data
          const filteredWagesOnlyDataLength = data.length;
          const filteredWagesOnlyTotalWages = data.reduce((prev: number, current: any) => {
            return Math.round(prev + current.wages);
          }, 0);
          const filteredWagesOnlyMean = Math.round(filteredWagesOnlyTotalWages / filteredDataLength);
          const filteredWagesOnlyVariance = data.reduce((prev: number, current: any) => {
            const filteredWagesOnlyDifference = current.wages - filteredWagesOnlyMean;
            const filteredWagesOnlysquaredDifference = Math.pow(filteredWagesOnlyDifference, 2);
            return prev + filteredWagesOnlysquaredDifference;
          }, 0) / filteredDataLength;
          const filteredWagesOnlyStandardDeviation = Math.round(Math.sqrt(filteredWagesOnlyVariance));

          setFilteredSummaryData({
            observations: filteredWagesOnlyDataLength,
            mean: filteredWagesOnlyMean,
            standard_deviation: filteredWagesOnlyStandardDeviation,
            total_sum: filteredWagesOnlyTotalWages,
          });

          // Filtered Wages Only Percentile Data
          const filteredWagesOnlySortedWages = data.map((item: any) => Math.round(item.wages)).sort((a: any , b: any) => a - b);
          const filteredWagesOnlyLowestWage = filteredWagesOnlySortedWages[0];
          const filteredWagesOnlyTwentyFifthindex = Math.floor(filteredWagesOnlySortedWages.length * 0.25);
          const filteredWagesOnlySeventyFifthindex = Math.floor(filteredWagesOnlySortedWages.length * 0.75);
          const filteredWagesOnlyTwentyFifthPercentile = filteredWagesOnlySortedWages[filteredWagesOnlyTwentyFifthindex];
          const filteredWagesOnlySeventyFifthPercentile = filteredWagesOnlySortedWages[filteredWagesOnlySeventyFifthindex];
          const filteredWagesOnlyMid = Math.floor(filteredWagesOnlyDataLength / 2);
          const filteredWagesOnlyMedian = filteredWagesOnlyDataLength % 2 !== 0 
            ? filteredWagesOnlySortedWages[filteredWagesOnlyMid] 
            : (filteredWagesOnlySortedWages[filteredWagesOnlyMid - 1] + filteredWagesOnlySortedWages[filteredWagesOnlyMid]) / 2;
          const filteredWagesOnlyMaximum = filteredWagesOnlySortedWages[filteredWagesOnlySortedWages.length - 1];

          // Update for Range Filters
          setFilteredPercentileData({
            minimum: filteredWagesOnlyLowestWage,
            twenty_fifth_percentile: filteredWagesOnlyTwentyFifthPercentile,
            median: filteredWagesOnlyMedian,
            seventy_fifth_percentile: filteredWagesOnlySeventyFifthPercentile,
            maximum: filteredWagesOnlyMaximum,
          })
          break;
      
        case "Benefits Only":
          // Summary Data
          const filteredBenefitsOnlyDataLength = data.length;
          const filteredBenefitsOnlyTotalWages = data.reduce((prev: number, current: any) => {
            return Math.round(prev + current.benefits);
          }, 0);
          const filteredBenefitsOnlyMean = Math.round(filteredBenefitsOnlyTotalWages / filteredBenefitsOnlyDataLength);
          const filteredBenefitsOnlyVariance = data.reduce((prev: number, current: any) => {
            const filteredBenefitsOnlyDifference = current.benefits - filteredBenefitsOnlyMean;
            const filteredBenefitsOnlySquaredDifference = Math.pow(filteredBenefitsOnlyDifference, 2);
            return prev + filteredBenefitsOnlySquaredDifference;
          }, 0) / filteredBenefitsOnlyDataLength;
          const filteredBenefitsOnlyStandardDeviation = Math.round(Math.sqrt(filteredBenefitsOnlyVariance));

          setFilteredSummaryData({
            observations: filteredBenefitsOnlyDataLength,
            mean: filteredBenefitsOnlyMean,
            standard_deviation: filteredBenefitsOnlyStandardDeviation,
            total_sum: filteredBenefitsOnlyTotalWages,
          });

          // Percentile Data
          const filteredBenefitsOnlySortedWages = data.map((item: any) => Math.round(item.benefits)).sort((a: any , b: any) => a - b);
          const filteredBenefitsOnlyLowestWage = filteredBenefitsOnlySortedWages[0];
          const filteredBenefitsOnlyTwentyFifthindex = Math.floor(filteredBenefitsOnlySortedWages.length * 0.25);
          const filteredBeneftitsOnlySeventyFifthindex = Math.floor(filteredBenefitsOnlySortedWages.length * 0.75);
          const filteredBenefitsOnlyTwentyFifthPercentile = filteredBenefitsOnlySortedWages[filteredBenefitsOnlyTwentyFifthindex];
          const filteredBenefitsOnlySeventyFifthPercentile = filteredBenefitsOnlySortedWages[filteredBeneftitsOnlySeventyFifthindex];
          const filteredBenefitsOnlyMid = Math.floor(filteredBenefitsOnlyDataLength / 2);
          const filteredBenefitsOnlyMedian = filteredBenefitsOnlyDataLength % 2 !== 0 
            ? filteredBenefitsOnlySortedWages[filteredBenefitsOnlyMid] 
            : (filteredBenefitsOnlySortedWages[filteredBenefitsOnlyMid - 1] + filteredBenefitsOnlySortedWages[filteredBenefitsOnlyMid]) / 2;
          const filteredBenefitsOnlyMaximum = filteredBenefitsOnlySortedWages[filteredBenefitsOnlySortedWages.length - 1];

          // Update for Range Filters
          setFilteredPercentileData({
            minimum: filteredBenefitsOnlyLowestWage,
            twenty_fifth_percentile: filteredBenefitsOnlyTwentyFifthPercentile,
            median: filteredBenefitsOnlyMedian,
            seventy_fifth_percentile: filteredBenefitsOnlySeventyFifthPercentile,
            maximum: filteredBenefitsOnlyMaximum,
          })
          break;
      }

    } else {
      setFilteredSummaryData({ observations: 0, mean: 0, standard_deviation: 0, total_sum: 0 });
      setFilteredPercentileData({ minimum: 0, twenty_fifth_percentile: 0, median: 0, seventy_fifth_percentile: 0, maximum: 0 });
    }

    setFilteredData(data);
  }, [masterData, selectedEmployeeNames, selectedEmployeeTitles,selectedGovernmentTypes, selectedEntityNames, selectedFiscalYears, chartType, minimumRangeSalary, maximumRangeSalary]); 

  // Sync tabulator with filtered data and prevent verticalFillMode errors during react re-renders
  useEffect(() => {
    const timer = setTimeout(() => {
      if (tabulatorRef.current && typeof tabulatorRef.current.getData === 'function') {
        const tableEl = tabulatorRef.current.element;
        if (tableEl && tableEl.offsetParent !== null) {
          tabulatorRef.current.replaceData(filteredData)
            .catch(() => {
            });
        }
      }
    }, 50);
    return () => clearTimeout(timer); // Clean up if table unmounts
  }, [filteredData]);

  // Updated Render D3 - Now includes X Axis Type functionality
  useEffect(() => {
    if (loading || !masterData.length || !chartRef.current) return;

    const svg = d3.select(chartRef.current);
    svg.selectAll("*").remove();

    // Create Data Key
    const chartTypeSelection =
      chartType === "Total Compensation" ? "total" :
      chartType === "Wages Only" ? "wages" :
      chartType === "Benefits Only" ? "benefits" : "total";

    // Filter based on range filter salary updates
    const filteredMasterData = masterData.filter(d => {
      const value = +d[chartTypeSelection];
      const inSalaryRange = value >= minimumRangeSalary && value <= maximumRangeSalary;
      const matchesName = (selectedEmployeeNames?.length ?? 0) > 0 
        ? selectedEmployeeNames!.includes(d.employee_name) 
        : true;
      const matchesTitle = (selectedEmployeeTitles?.length ?? 0) > 0 
        ? selectedEmployeeTitles!.includes(d.title) 
        : true;
      return inSalaryRange && matchesName && matchesTitle;
    });

    const margin = { top: 10, right: 10, bottom: 60, left: 80 };
    const width = containerRef.current?.clientWidth ?? 1200;
    const height = 500;

    // Define the X Domain
    const xDomain: [number, number] = filteredMasterData.length === 1 
      ? [+filteredMasterData[0][chartTypeSelection] - 5000, +filteredMasterData[0][chartTypeSelection] + 5000]
      : [minimumRangeSalary, maximumRangeSalary];

    // Define the X Scale Type
    let x: d3.ScaleContinuousNumeric<number, number>;

    if (xAxisType === "Log2 Transform") {
      // Force log to start at 1 or greater
      const logStart = xDomain[0] <= 0 ? 1 : xDomain[0];
      x = d3.scaleLog()
        .base(2)
        .domain([logStart, xDomain[1]])
        .range([margin.left, width - margin.right]);
    } else {
      x = d3.scaleLinear()
        .domain(xDomain)
        .range([margin.left, width - margin.right])
        .nice();
    }

    // Histogram Bins
    const bins = d3.bin<MostCommonJob, number>()
      .value((d) => +d[chartTypeSelection])
      .domain(x.domain() as [number, number])
      .thresholds(
        (xAxisType === "Log2 Transform"
          ? x.ticks(numberOfBins || 20)
          : (!numberOfBins 
              ? (filteredMasterData.length < 10 ? filteredMasterData.length : 67) 
              : (numberOfBins > 100 ? 100 : numberOfBins))
        ) as any
      )
      (filteredMasterData);

    // Create Y Scale
    const y = d3.scaleLinear()
      .domain([0, d3.max(bins, (d) => d.length) || 0])
      .nice()
      .range([height - margin.bottom, margin.top]);

    // Add dimensions to SVG Container and Draw Chart
    svg.attr("width", width)
      .attr("height", height)
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("style", "max-width: 100%; height: auto;");

    // Create Tooltips
    const tooltip = d3.select("body").append("div").attr("class", "tooltip").style("opacity", 0)
      .style("position", "absolute").style("pointer-events", "none").style("background-color", "#fff")
      .style("border", "1px solid #ccc").style("padding", "12px").style("border-radius", "6px").style("z-index", "1000");

    svg.append("g")
      .attr("fill", "#ffc63a")
      .selectAll()
      .data(bins)
      .join("rect")
      .attr("x", (d) => x(d.x0 || 0) + 1)
      .attr("width", (d) => Math.max(0, x(d.x1 || 0) - x(d.x0 || 0) - 1))
      .attr("y", (d) => y(d.length))
      .attr("height", (d) => y(0) - y(d.length))
      .on("mouseover", (event) => {
        tooltip.style("opacity", 1);
        d3.select(event.currentTarget).attr("fill", "#e6b334");
      })
      .on("mousemove", (event, d) => {
        tooltip.html(`
          <div style="font-weight: bold;">Range: $${d.x0?.toLocaleString()} - $${d.x1?.toLocaleString()}</div>
          <div>Count: ${d.length} employees</div>
        `).style("left", (event.pageX + 15) + "px").style("top", (event.pageY - 28) + "px");
      })
      .on("mouseout", (event) => {
        tooltip.style("opacity", 0);
        d3.select(event.currentTarget).attr("fill", "#ffc63a");
      });

    const xTickFormat = xAxisType === "Log2 Transform" ? d3.format(",d") : d3.format("$.2s");

    // Add X axis and label
    svg.append("g")
      .attr("transform", `translate(0,${height - margin.bottom})`)
      .call(d3.axisBottom(x).ticks(width / 80).tickSizeOuter(0).tickFormat(xTickFormat as any))
      .call((g) => g.append("text")
        .attr("x", (width - margin.left - margin.right) / 2 + margin.left)
        .attr("y", margin.bottom - 10)
        .attr("fill", "currentColor")
        .attr("text-anchor", "middle")
        .attr("font-weight", "bold")
        .text(`${chartType} ($)`));

    // Add Y axis and label
    svg.append("g")
      .attr("transform", `translate(${margin.left},0)`)
      .call(d3.axisLeft(y).ticks(height / 40))
      .call((g) => g.select(".domain").remove())
      .call((g) => g.append("text")
        .attr("transform", "rotate(-90)")
        .attr("x", -(height - margin.top - margin.bottom) / 2 - margin.top)
        .attr("y", -margin.left + 15)
        .attr("fill", "currentColor")
        .attr("text-anchor", "middle")
        .attr("font-weight", "bold")
        .text("Number of Employees"));

    // Return the SVG element
    return () => { 
      tooltip.remove(); 
    };
  }, [masterData, loading, selectedEmployeeNames, selectedEmployeeTitles, selectedGovernmentTypes, selectedEntityNames, selectedFiscalYears, chartType, minimumRangeSalary, maximumRangeSalary, numberOfBins, xAxisType]);

  // Update number of bins based on numberOfBins and length of observations
  useEffect(() => {
    const count = filteredSummaryData?.observations ?? 0;
    if (count < 2) {
      setNotEnoughBins(true);
    } else {
      setNotEnoughBins(false);
      const idealBins = count < 67 ? count : 67;
      setNumberOfBins(idealBins);
    }
  }, [filteredSummaryData?.observations]); 

  // Handle Salary Range Updates for Range Slider Form
  const handleMinSalaryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.min(Number(event.target.value), maximumRangeSalary - 1000);
    setMinimumRangeSalary(value);
  }

  const handleMaxSalaryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.max(Number(e.target.value), minimumRangeSalary + 1000);
    setMaximumRangeSalary(value);
  };
  
  // Reusable Statistics Card Component
  const StatisticsCard: React.FC<StatisticsCardProps> = ({ title, value, bg = "secondary", isCurrency = true }) => {
    const formattedValue = value !== undefined && value !== null 
      ? (isCurrency ? `$${value.toLocaleString()}` : value.toLocaleString()) 
      : (isCurrency ? "$0" : "0");

    return (
      <Col xs={6} md={3} lg={true} className="mb-4">
        <Card bg={bg} className={`h-100 ${bg === "secondary" ? "text-white" : ""}`}>
          <Card.Body className="text-center d-flex flex-column justify-content-center p-2">
            <Card.Title className="fs-5 mb-2">{formattedValue}</Card.Title>
            <Card.Text className="small text-uppercase fw-bold" style={{ fontSize: '0.75rem', opacity: 0.8 }}>
              {title}
            </Card.Text>
          </Card.Body>
        </Card>
      </Col>
    );
  };

  // Updated memoized options for MultiSelectAsync dropdown forms
  const employeeTitleOptions = useMemo(() =>
    (allEmployeeTitles || []).map(title => ({ value: title, label: title})),
  [allEmployeeTitles]);

  const employeeNameOptions = useMemo(() =>
    (allEmployeeNames || []).map(name => ({ value: name, label: name})),
  [allEmployeeNames]);

  const governmentTypeOptions = useMemo(() => 
    (allGovernmentTypes || []).map(type => ({ value: type, label: type })), 
  [allGovernmentTypes]);

  const fiscalYearOptions = useMemo(() => 
    (allFiscalYears || []).map(year => ({ value: year.toString(), label: year.toString() })), 
  [allFiscalYears]);

  const entityNameOptions = useMemo(() => 
    (allEntityNames || []).map(name => ({ value: name, label: name })), 
  [allEntityNames]);

  // Add options for chart type radio form
  const chartTypeOptions = ["Total Compensation", "Wages Only", "Benefits Only"];

  // Add options for Transform X-Axis radio form
  const transformXAxisOptions = ["Normal Scale", "Log2 Transform"];

  return (
    <>
      <HeaderComponent/>
      <FeatureNav/>
      <BreadCrumbs/>
      <Container fluid as="main">
        <Row className="mb-4 px-4">
          <Col xs={12} className="px-0">
            <Card bg="secondary" text="white" style={{ border: "none", borderRadius: "0" }}>
              <Card.Body style={{ justifyItems: "center", textAlign: "center" }}>
              <div className="tu_tabHeader">
                <div className="tu-tabHeader__titleRow">
                  <h1 className="tu-tabHeader__title">
                  Local Government Jobs</h1>
                    <button
                    type="button"
                    className="tu-tabHeader__infoBtn"
                    onClick={() => setShowHeaderInfo(!showHeaderInfo)}
                    aria-label="Local Government Jobs info"
                  >
                    <i className="fas fa-info"/>
                    </button>
                  </div>
                </div>
                <Collapse in={showHeaderInfo}>
                  <div>
                  <p className="lead mt-2 text-center">
                    The Office of the State Auditor has manually grouped comparable jobs titles under a common name to allow utahns to compare compensation packages across entities. To use this tool, select a job title.
                  </p>
                  </div>
                </Collapse>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Loader */}
        {loading ? <Loader /> : <></>}

        { /* Search Bar */}
        <Row className="px-4">
          {/* Select Job Title */}
          <Col xs={12} className="px-0">
            <Card>
              <Card.Header className="bg-secondary text-white" style={{ border: "none" }}>
                <h3 className="text-white">Search</h3>
              </Card.Header>
              <Card.Body className="p-2">
                <Row>
                  <Col xs={12} sm={12} md={4} lg={4} xl={4} className="my-2">
                    <h5>
                      <i className="fas fa-briefcase"/>
                      {" "}Select Job Title
                    </h5>
                    <SingleSelectAsync
                      value={selectedJob}
                      key={curatedOptions.length}
                      onChange={(newValue) => setSelectedJob(newValue)}
                      loadOptions={loadJobOptions}
                      type="title"
                      defaultOptions={curatedOptions}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filters Section */}
        <Row className="px-4">
          <Col sm={12} md={12} lg={4} xl={3} className="h-100 highest-paid-jobs-container ps-lg-0 pe-lg-2">
            <Card className="mt-4">
              <Card.Header className="bg-secondary text-white">
                <h3 className="text-white">Filter</h3>
              </Card.Header>
              <Card.Body className="px-0 mx-0 pt-0 mb-2">
                <Row className="px-4">
                  {/* Title Filter */}
                  <Col xs={12} className="mt-4">
                    <h5>
                      <i className="fas fa-briefcase"/>
                      {" "}Filter Title
                      <MultiSelectAsync
                        options={employeeTitleOptions}
                        defaultOptions={employeeTitleOptions}
                        value={selectedEmployeeTitles ? selectedEmployeeTitles.map(title => ({ value: title, label: title })) : []} 
                        onChange={(selected) => {
                          const titles = selected ? selected.map(s => s.value) : [];
                          setSelectedEmployeeTitles(titles);
                        }}
                        loadOptions={async (inputValue) => {
                          if (!inputValue) return employeeTitleOptions;
                          return employeeTitleOptions.filter(o =>
                            o.label.toLowerCase().includes(inputValue.toLowerCase())
                          );
                        }}
                        closeMenuOnSelect={false}
                      />
                    </h5>
                  </Col>
                  {/* End Title Filter */}

                  {/* Employee Filter */}
                  <Col xs={12} className="mt-4">
                    <h5>
                      <i className="fas fa-user"/>
                      {" "}Filter Employee
                      <MultiSelectAsync
                        options={employeeNameOptions}
                        defaultOptions={employeeNameOptions}
                        value={selectedEmployeeNames ? selectedEmployeeNames.map(name => ({ value: name, label: name})) : []}
                        isSearchable={true}
                        onChange={(selected) => {
                          const names = selected ? selected.map(s => s.value) : [];
                          setSelectedEmployeeNames(names);
                        }}
                        loadOptions={async (inputValue) => {
                          if (!inputValue) return employeeNameOptions;
                          return employeeNameOptions.filter(o =>
                            o.label.toLowerCase().includes(inputValue.toLowerCase())
                          );
                        }}
                        closeMenuOnSelect={false}
                      />
                    </h5>
                  </Col>
                  {/* End Employee Filter */}

                  {/* Government Type Filter */}
                  <Col xs={12} className="mt-4">
                    <h5>
                      <i className="fas fa-briefcase"/>
                      {" "}Filter Government Type
                      <MultiSelectAsync
                        options={governmentTypeOptions} 
                        defaultOptions={governmentTypeOptions}
                        value={selectedGovernmentTypes? selectedGovernmentTypes.map(type => ({
                          value: type,
                          label: type
                        })) : []}
                        isSearchable={true}
                        onChange={(selected) => {
                          const governmentTypeOptions = selected ? selected.map(s => s.value) : [];
                          setSelectedGovernmentTypes(governmentTypeOptions);
                        }}
                        loadOptions={async (inputValue) => {
                          if (!inputValue) return governmentTypeOptions;
                          return governmentTypeOptions.filter(o => 
                            o.label.toLowerCase().includes(inputValue.toLowerCase())
                          );
                        }}
                        closeMenuOnSelect={false}
                      />
                    </h5>
                  </Col>
                  {/* End Government Type Filter */}

                  {/* Entity Filter */}
                  <Col xs={12} className="mt-4">
                    <h5>
                      <i className="fas fa-landmark"/>
                      {" "}Filter Entity
                      <MultiSelectAsync
                        options={entityNameOptions} 
                        defaultOptions={entityNameOptions}
                        value={selectedEntityNames ? selectedEntityNames?.map(name => ({
                          value: name,
                          label: name,
                        })) : []}
                        isSearchable={true}
                        onChange={(selected) => {
                          const entityNameOptions = selected ? selected.map(s => s.value) : [];
                          setSelectedEntityNames(entityNameOptions);
                        }}
                        loadOptions={async (inputValue) => {
                          if (!inputValue) return entityNameOptions;
                          return entityNameOptions.filter(o => 
                            o.label.toLowerCase().includes(inputValue.toLowerCase())
                          );
                        }}
                        closeMenuOnSelect={false}
                      />
                    </h5>
                  </Col>
                  {/* End Entity Filter */}

                  {/* Fiscal Year Filter */}
                  <Col xs={12} className="mt-4">
                    <h5>
                      <i className="fas fa-calendar"/>
                      {" "}Filter Year
                      <MultiSelectAsync
                        options={fiscalYearOptions}
                        defaultOptions={fiscalYearOptions}
                        value={
                          selectedFiscalYears?.map(year => ({
                            value: year.toString(),
                            label: year.toString(),
                          })) || []
                        }
                        isSearchable={true}
                        onChange={(selected) => {
                          const values = selected ? selected.map(s => Number(s.value)) : [];
                          setSelectedFiscalYears(values);
                        }}
                        loadOptions={async (inputValue) => {
                          if (!inputValue) return fiscalYearOptions;
                          return fiscalYearOptions.filter(o =>
                            o.label.toString().toLowerCase().includes(inputValue.toLowerCase())
                          );
                        }}
                        closeMenuOnSelect={false}
                      />
                    </h5>
                  </Col>
                  {/* End Fiscal Year Filter */}

                  {/* Range Filter */}
                  <Col xs={12} className="mt-4">
                    <h5>
                      <i className="fas fa-dollar-sign"/>
                      {" "}Filter Range
                    </h5>
                    <div className="filter-range-slider position-relative" style={{ height: "40px"}}>
                      {/* Min Slider */}
                      { rangeFilterCeiling > 0 ? (
                      <>
                        <label htmlFor="min-range-slider" className="sr-only" style={{ display: "none"}}>Minimum Salary</label>
                        <input 
                          type="range"
                          id="min-range-slider"
                          aria-label="minimum-salary-range" 
                          min={rangeFilterFloor}
                          max={rangeFilterCeiling}
                          value={minimumRangeSalary}
                          onChange={handleMinSalaryChange}
                          className="form-range position-absolute w-100"
                          style={{ 
                            zIndex: (minimumRangeSalary > (rangeFilterCeiling / 2)) ? 10 : 5,
                            pointerEvents: "none",
                            background: "transparent"
                          }}
                        />
                      </>
                      ) : 
                      (<></>)
                      }
                      {/* Max Slider */}
                      { maximumRangeSalary > 0 ? (
                        <>
                          <label htmlFor="max-range-slider" className="sr-only" style={{ display: "none"}}>Maximum Salary</label>
                          <input 
                            type="range"
                            id="max-range-slider"
                            aria-label="maximum-salary-range"
                            aria-valuetext={`$${maximumRangeSalary}`}  
                            min={rangeFilterFloor}
                            max={rangeFilterCeiling}
                            value={maximumRangeSalary}
                            onChange={handleMaxSalaryChange}
                            className="form-range position-absolute w-100"
                            style={{ 
                              zIndex: 4,
                              pointerEvents: "none",
                              background: "transparent" 
                            }}
                          />
                        </>
                      ) : 
                      (<></>)
                      }
                    </div>
                    <div className="d-flex justify-content-between mt-2">
                      <span className="badge bg-light text-dark border">${minimumRangeSalary.toLocaleString()}</span>
                      <span className="badge bg-light text-dark border">${maximumRangeSalary.toLocaleString()}</span>
                    </div>
                  </Col>
                  {/* End Range Filter */}

                  {/* Chart Type Filter */}
                  <Col xs={12} className="mt-4">
                    <h5>
                      <i className="fas fa-chart-area"/>
                      {" "}Filter Chart Type
                      <Form>
                        <div className="mb-2">
                          {chartTypeOptions.map((option) => (
                            <Form.Check
                              key={option}
                              inline
                              type="radio"
                              label={option}
                              name="chartTypeGroup"
                              id={`radio-${option.replace(/\s+/g, '-').toLowerCase()}`}
                              checked={chartType === option}
                              style={{display: "block"}}
                              onChange={() => setChartType(option as "Total Compensation" | "Wages Only" | "Benefits Only")}
                            />
                          ))}
                        </div>
                      </Form>
                    </h5>
                  </Col>
                  {/* End Chart Type Filter */}

                  {/* Transform X-Axis Filter */}
                  <Col xs={12} className="mt-4">
                    <h5>
                      <i className="fas fa-chart-line"/>
                      {" "}Transform X Axis
                      <Form>
                        <div className="mb-2">
                          {transformXAxisOptions.map((option) => (
                            <Form.Check
                              key={option}
                              inline
                              type="radio"
                              label={option}
                              name="transformXAxisGroup"
                              id={`radio-${option.replace(/\s+/g, '-').toLowerCase()}`}
                              checked={xAxisType === option}
                              style={{display: "block"}}
                              onChange={() => setAxisType(option as "Normal Scale" | "Log2 Transform")}
                            />
                          ))}
                        </div>
                      </Form>
                    </h5>
                  </Col>
                  {/* End Transform X-Axis Filter */}

                </Row>
              </Card.Body>
            </Card>
          </Col>

          {/* Updated Statistics Section */}
          <Col xs={12} lg={8} xl={9} className="h-100 highest-paid-jobs-container pe-lg-0 ps-lg-2">
            <Card className="mt-4 shadow-sm">
              <Card.Header className="bg-secondary text-white">
                <h3 className="text-white">Statistics</h3>
              </Card.Header>
              <Card.Body style={{ paddingBottom: "44.5px", paddingTop: "44.5px" }}>

                {/* Summary Section */}
                <h4 className="mb-3">Summary</h4>
                <Row className="gx-2 gx-md mb-4">
                  <StatisticsCard title="Observations" value={summaryData?.observations} isCurrency={false}/>
                  <StatisticsCard title="Mean" value={summaryData?.mean}/>
                  <StatisticsCard title="Standard Deviation" value={summaryData?.standard_deviation} />
                  <StatisticsCard title="Total Sum" value={summaryData?.total_sum} />
                </Row>

                {/* Filtered Summary Section */}
                {filteredSummaryData && (
                  <>
                    <h4 className="mb-3">Filtered Summary</h4>
                    <Row className="gx-2 gx-md mb-4">
                      <StatisticsCard title="Filtered Observations" value={filteredSummaryData?.observations} isCurrency={false} bg="primary"/>
                      <StatisticsCard title="Mean" value={filteredSummaryData?.mean} bg="primary"/>
                      <StatisticsCard title="Standard Deviation" value={filteredSummaryData?.standard_deviation} bg="primary"/>
                      <StatisticsCard title="Total Sum" value={filteredSummaryData?.total_sum} bg="primary"/>
                    </Row>
                  </>
                )}

                {/* Percentile Section */}
                <h4 className="mb-3">Percentiles</h4>
                <Row className="gx-2 gx-md mb-4">
                  <StatisticsCard title="Minimum" value={percentileData?.minimum} />
                  <StatisticsCard title="25th Percentile" value={percentileData?.twenty_fifth_percentile} />
                  <StatisticsCard title="Median"  value={percentileData?.median} />
                  <StatisticsCard title="75th Percentile" value={percentileData?.seventy_fifth_percentile} />
                  <StatisticsCard title="Maximum"  value={percentileData?.maximum}  />
                </Row>

                {/* Filtered Percentiles Section */}
                {filteredPercentileData && (
                  <>
                    <h4 className="mb-3">Filtered Percentiles</h4>
                    <Row className="gx-2 gx-md mb-4">
                      <StatisticsCard title="Filtered Minimum" value={filteredPercentileData?.minimum} bg="primary"/>
                      <StatisticsCard title="25th Percentile" value={filteredPercentileData?.twenty_fifth_percentile} bg="primary"/>
                      <StatisticsCard title="median" value={filteredPercentileData?.median} bg="primary"/>
                      <StatisticsCard title="75h Percentile" value={filteredPercentileData?.seventy_fifth_percentile} bg="primary"/>
                      <StatisticsCard title="Maximum" value={filteredPercentileData?.maximum} bg="primary"/>
                    </Row>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* D3 Chart */}
          <Row className="pt-4 mx-0 px-0">
            <Col xs={12} className="px-0">
              <Card bg="white">
                <Card.Header className="bg-secondary text-white">
                  <h3 className="text-white">Cumulative Distribution (Histogram)</h3>
                </Card.Header>
                <Card.Body>
                  <div id="chart-container" ref={containerRef} style={{ width: "100%" }}>
                    {(!masterData || (masterData?.length ?? 0) <= 1) && (
                      <p className="text-lead text-center py-5">
                        Data must have at least 2 observations to plot a histogram. Please select a job title or refine your filters.
                      </p>
                    )}

                    <div className={(masterData?.length ?? 0) > 1 ? "d-block" : "d-none"}>
                      <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
                        <div
                          className="btn btn-secondary"
                          id="btn-chart-download"
                          onClick={() => {
                            if (!chartRef.current) return;
                            const serializer = new XMLSerializer();
                            const svgString = serializer.serializeToString(chartRef.current);
                            const canvas = document.createElement('canvas');
                            const context = canvas.getContext('2d');
                            const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
                            const url = URL.createObjectURL(svgBlob);
                            const img = new Image();
                            img.onload = () => {
                              canvas.width = img.width;
                              canvas.height = img.height;
                              if (context) {
                                context.fillStyle = "white";
                                context.fillRect(0, 0, canvas.width, canvas.height);
                                context.drawImage(img, 0, 0);
                              }
                              URL.revokeObjectURL(url);
                              const pngUrl = canvas.toDataURL('image/png');
                              const a = document.createElement('a');
                              a.download = `highest_paid_jobs_data.png`;
                              a.href = pngUrl;
                              a.click();
                            };
                            img.src = url;
                          }}
                        >
                          Download
                        </div>
                      </div>

                      <Form className="mt-4 w-25">
                        <Form.Group className="mb-3">
                          <Form.Label>Number of Bins</Form.Label>
                          <Form.Control
                            type="number"
                            value={numberOfBins}
                            onChange={(event) => {
                              const value = event.target.value;
                              setNumberOfBins(value === "" ? 67 : parseInt(value, 10));
                            }}
                            placeholder={numberOfBins.toLocaleString()}
                          />
                        </Form.Group>
                      </Form>

                      <svg
                        ref={chartRef}
                        width="100%"
                        height={containerRef.current ? containerRef.current.clientWidth * 0.6 : 600}
                        viewBox={`0 0 ${containerRef.current?.clientWidth ?? 1200} ${containerRef.current?.clientWidth ? containerRef.current.clientWidth * 0.6 : 600}`}
                        preserveAspectRatio="xMidYMid meet"
                      />
                      
                      <div style={{ marginTop: "12px", fontWeight: "bold", fontSize: "16px", textAlign: "center" }} />
                      <div ref={legendRef} style={{ marginTop: "12px", display: "flex", flexWrap: "wrap" }} />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>	

          {/* Tabulator Table */}
          <Row className="row py-4 px-0 mx-auto">
            <Col xs={12} className="px-0">
              <Card bg="white">
                <Card.Header style={{ backgroundColor: "#2e8b8b"}} className="text-white">
                  <h3 className="text-white">Data</h3>
                </Card.Header>
                <Card.Body>
                  <div className="mb-2 d-flex gap-2">
                    <button className="btn btn-secondary" onClick={() => handleDownload("csv")}>CSV</button>
                    <button className="btn btn-secondary" onClick={() => handleDownload("xlsx")}>Excel</button>
                    <button className="btn btn-secondary" onClick={() => handleDownload("html")}>HTML</button>
                    <button className="btn btn-secondary" onClick={() => handleDownload("json")}>JSON</button>
                  </div>
                  <div style={{ width: "100%",
                    position: "relative"
                  }}>
                    {/* Loading */}
                    {loading && (
                      <div className="text-center py-5">
                        <div className="text-primary" role="status"></div>
                        {/* Loader */}
                        {loading ? <Card.Text><Loader /></Card.Text> : <></>}
                      </div>
                    )}

                    {/* No Data Available */}
                    {!loading && (!filteredData || filteredData.length === 0) && (
                      <Card.Text className="text-center py-5">No Data available - please select a job title</Card.Text>
                    )}

                    {/* Table Container - Rendered but hidden if not needed */}
                    <div 
                      ref={tableRef} 
                      className={`table-light ${(!loading && (filteredData?.length ?? 0) > 0) ? 'd-block' : 'd-none'}`} 
                      style={{ minHeight: 400 }}  
                    />
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
				</Row>
      </Container>
      <FooterSection />
    </>
  )
}

export default EmployeesLocalGovernmentJobs;